import config from 'config';
import { OrgAnalyticsResultItem, TimeSignalAnalytic } from 'graphql/types';

const colorSet = [
  '#880e4f',
  '#4a148c',
  '#304ffe',
  '#006064',
  '#827717',
  '#3e2723',
  '#212121',
  '#00c853',
  '#00e5ff',
  '#ab47bc',
  '#cddc39',
  '#4caf50',
  '#ad1457',
  '#616161',
  '#8d6e63',
  '#ec407a',
  '#80cbc4',
  '#546e7a'
];

const manualNoteSourceColor = '#ef6c00';
const getChartColor = (index: number): string => {
  const colorSetLength = colorSet.length;
  if (index > colorSetLength - 1) {
    return colorSet[(index % colorSetLength) - 1];
  } else return colorSet[index];
};

export function transformAnalyticsData(
  data: TimeSignalAnalytic | undefined,
  manualEntries: string
): {
  x: string;
  y: number;
  name: string;
  value: number;
  color: string;
  imgSrc: string | undefined;
}[][] {
  if (!data) {
    return [];
  }
  const dataSet = data.dimensions.map((_data, index) => {
    let color: string;
    const isManualSource = _data.id === config.clientId;
    if (isManualSource) {
      color = manualNoteSourceColor;
    } else {
      color = getChartColor(index);
    }
    return {
      x: 'SOURCE',
      name: isManualSource ? manualEntries : _data.name,
      imgSrc:
        _data.metadata?.find((x) => x?.key === 'image')?.value ?? undefined,
      y: data.metrics[index].totalDuration,
      value: data.metrics[index].totalDuration,
      color: color
    };
  });

  const totals = dataSet.reduce((memo, curr) => {
    return memo + curr.y;
  }, 0);
  dataSet.sort((a, b) => b.value - a.value);
  return dataSet.map((d) => {
    return [
      {
        x: d.x,
        y: (d.y / totals) * 100,
        name: d.name,
        value: d.value,
        color: d.color,
        imgSrc: d.imgSrc
      }
    ];
  });
}

export function transformAnalyticsDataSupervisor(
  data: (OrgAnalyticsResultItem | undefined | null)[] | undefined,
  manualEntries: string
): {
  x: string;
  y: number;
  name: string;
  value: number;
  color: string;
  imgSrc: string | undefined;
  id: string;
}[][] {
  if (!data) {
    return [];
  }
  const dataSet = data.map((_data, index) => {
    let color: string;
    if (_data) {
      const isManualSource = _data?.dimension.id === config.clientId;
      if (isManualSource) {
        color = manualNoteSourceColor;
      } else {
        color = getChartColor(index);
      }
      return {
        x: 'SOURCE',
        name: isManualSource ? manualEntries : _data.dimension.name,
        imgSrc:
          _data?.dimension?.metadata?.find((x) => x?.key === 'image')?.value ??
          undefined,
        y: _data.metrics.totalDuration,
        value: _data.metrics.totalDuration,
        color: color,
        id: _data.dimension.id
      };
    }
  });

  const totals = dataSet.reduce((memo: number, curr: any) => {
    return memo + curr.y;
  }, 0);

  return dataSet.map((d: any) => {
    return [
      {
        x: d.x,
        y: (d.y / totals) * 100,
        name: d.name,
        value: d.value,
        color: d.color,
        imgSrc: d.imgSrc,
        id: d.id
      }
    ];
  });
}

export function trendsData(
  data: (OrgAnalyticsResultItem | undefined | null)[] | undefined
): Record<string, number> {
  return (
    data?.reduce((acc: any, item: any) => {
      if (item?.dimension?.id) {
        const id = item?.dimension?.id;
        acc[id] = item.metrics.totalDuration;
      }
      return acc;
    }, {} as Record<string, number>) || {}
  );
}
